import type { BonierStand } from './bonierung';
import type { AnmeldeWaechter } from './geraeteanmeldung';
import type { Geraetezeuge, GeraetezeugeAblage } from './geraetezeuge';
import type { KassenProfil } from './schnellwahl';
import type { Position, Steuersatz } from './warenkorb';
import type { ZahlungMitBeleg } from './zahlung-kandidaten';

/**
 * Typisierter Client für die Bilanx-API. Die PWA hält keine Fachlogik: Belege
 * entstehen ausschließlich serverseitig im RKSV-Kern (Regel 2), Kartenzahlungen
 * laufen über das Zahlungsprotokoll der API (Regel 3).
 */
/**
 * F-4: Meldung zu `code: 'faehigkeit_gewicht_fehlt'` der Positionsprüfung des Servers
 * (packages/api/src/wiegepositionen.ts `formFehlerAntwort`) – Parken, Wiederaufnahme,
 * Beleg und Zahlungsstart. Der Bon bleibt dabei unverändert stehen.
 */
export const MELDUNG_GEWICHT_FEHLT =
  'Diese Kasse kann noch keine Wiegeartikel verkaufen – bitte Kassen-App aktualisieren.';

/**
 * Verständliche Meldung zu einer 422-Antwort der Positionsprüfung; null = kein solcher Code
 * (dann gilt der Text des Servers).
 */
export function formFehlerMeldung(daten: Readonly<Record<string, unknown>>): string | null {
  if (daten.code === 'faehigkeit_gewicht_fehlt') {
    return MELDUNG_GEWICHT_FEHLT;
  }
  if (daten.code === 'positionen_ungueltig') {
    const abweichungen = Array.isArray(daten.abweichungen)
      ? daten.abweichungen.filter((a): a is string => typeof a === 'string')
      : [];
    const grund =
      abweichungen.length > 0
        ? abweichungen.join('; ')
        : typeof daten.fehler === 'string'
          ? daten.fehler
          : '';
    return `Bon so nicht buchbar – bitte Positionen prüfen${grund === '' ? '' : `: ${grund}`}`;
  }
  return null;
}

/**
 * Offline-Nachsendung: Der Server meldet Formfehler je Beleg nur als Text (ohne `code`). Fehlt
 * die Fähigkeit „gewicht“, dieselbe Meldung wie online; sonst der Text des Servers.
 */
export function offlineFehlerText(fehler: string): string {
  return fehler.includes('Fähigkeit „gewicht“') ? MELDUNG_GEWICHT_FEHLT : fehler;
}

/**
 * Codex N-4: Der Server hat Offline-Belege quittiert, aber der Zeuge dazu ist nicht dauerhaft
 * gespeichert. Die Synchronisation endet hier – der Aufrufer leert den Puffer nicht; der nächste
 * Lauf sendet dieselben Belege erneut (Server: „vorhanden“) und merkt den Zeugen dann.
 */
export class ZeugeNichtGespeichert extends Error {
  constructor(ursache: unknown) {
    super(
      `Nachweis des bestätigten Belegs nicht gespeichert – offline erfasste Belege bleiben auf dem Gerät (${
        ursache instanceof Error ? ursache.message : String(ursache)
      })`,
    );
  }
}

/**
 * av5 V-8: Meldung, wenn der Server 426 meldet, weil das Gerät den Nachweis des letzten Belegs
 * nicht lesen konnte (Gerätespeicher), nicht weil die App zu alt ist. Kein Beleg; offline
 * erfasste Belege bleiben auf dem Gerät.
 */
export const GERAETESPEICHER_NICHT_LESBAR =
  'Gerätespeicher nicht lesbar: Der Nachweis des letzten Belegs fehlt – es entsteht kein Beleg, offline erfasste Belege bleiben auf dem Gerät. Kassen-App neu starten; hilft das nicht, den Betreuer verständigen (keine App-Daten löschen).';

export class ApiFehler extends Error {
  override readonly name = 'ApiFehler';

  constructor(
    readonly status: number,
    nachricht: string,
    /** P1-8: JSON-Antwort des Servers (Kennzeichen wie `infrastruktur`, `signaturdienst`). */
    readonly daten: Readonly<Record<string, unknown>> | null = null,
  ) {
    super(nachricht);
  }
}

/**
 * P1-5: Meldung bei Zeitüberschreitung (Status 0 – Ergebnis unklar, wie ein Netzfehler); als
 * bestehende Meldung formuliert (Codex-Hinweis: kein neuer Text an der Kasse).
 */
export const ZEITUEBERSCHREITUNG = 'Kasse nicht erreichbar';

/**
 * P1-5: Zeitüberschreitung eines API-Aufrufs – Status 0 wie ein Netzfehler (Ergebnis unklar).
 * Eigene Klasse, damit die Puffer-Entscheidung sie ohne Schalter ausnehmen kann (Codex 1).
 */
export class ApiZeitueberschreitung extends ApiFehler {
  constructor() {
    super(0, ZEITUEBERSCHREITUNG);
  }
}

/**
 * P1-5 (O1-L6): Zeitlimits der API-Aufrufe in ms. Eine Zeitüberschreitung gilt als „Ergebnis
 * unklar“ und wird wie ein Netzfehler behandelt (ApiFehler mit Status 0): ein Barverkauf wird
 * unter derselben Belegnummer gepuffert, ein Kartenstart gilt als unklar („Zahlung prüfen“).
 */
export interface Zeitlimits {
  /**
   * Belegerstellung (POST …/belege). Vorgabe: Sicherheitslimit `BELEG_SICHERHEITSLIMIT_MS`
   * (nur echte Hänger); kurz (8 s) nur mit Schalter `VITE_OFFLINE_ERWEITERT` – siehe
   * `belegZeitlimit`.
   */
  beleg: number;
  /** Kleine Abfragen: Status, Gesundheit, Gerätestatus, Syncstand, Zahlungsstand. */
  kurz: number;
  /** Zahlungsstart und Terminalvorgänge (Storno, Rückerstattung, Klärung). */
  zahlung: number;
  /** Große bzw. signierende Vorgänge: Synchronisation, Artikelstamm, Berichte, Sonderbelege. */
  gross: number;
  /** Alle übrigen Aufrufe. */
  standard: number;
}

/**
 * Codex-Nachprüfung (KRITISCH 1): Sicherheitslimit der Belegerstellung ohne Schalter. Es liegt
 * deutlich über der Summe der serverseitigen Zeitlimits eines Belegs, damit eine langsame, aber
 * erfolgreiche Signatur nie in den Klärungsweg fällt (wie am Stand 58a387a ohne Zeitlimit):
 * A-Trust je Aufruf 15 s (a-trust.ts) – Verbinden (Zertifikat/VDA parallel), Anmelden,
 * Signieren und bei abgelaufener Sitzung erneut Anmelden und Signieren: bis 75 s; dazu OpenBao
 * Transit 10 s (openbao.ts) und Datenbank. 120 s fängt nur echte Hänger („schwarzes Loch“).
 */
export const BELEG_SICHERHEITSLIMIT_MS = 120_000;

/** P1-5: kurzes Beleg-Zeitlimit – nur mit Schalter (dann führt die Überschreitung in den Puffer). */
export const BELEG_ZEITLIMIT_ERWEITERT_MS = 8_000;

/** Zeitlimit der Belegerstellung je nach Schalter `VITE_OFFLINE_ERWEITERT`. */
export function belegZeitlimit(erweitert: boolean): number {
  return erweitert ? BELEG_ZEITLIMIT_ERWEITERT_MS : BELEG_SICHERHEITSLIMIT_MS;
}

export const ZEITLIMITS: Zeitlimits = {
  beleg: BELEG_SICHERHEITSLIMIT_MS,
  kurz: 5_000,
  zahlung: 20_000,
  gross: 60_000,
  standard: 15_000,
};

/**
 * N1: Aufrufe, die einen Verkauf bzw. eine neue Geldbewegung auslösen – Beleg erstellen,
 * Kartenzahlung starten, Rückerstattung, Lieferdienst-Umsatz erfassen bzw. stornieren. Während eines Verkaufsstopps sendet die Kasse sie nicht.
 */
export function istKassierweg(methode: string, pfad: string): boolean {
  const ohneAbfrage = pfad.split('?')[0] ?? pfad;
  return (
    methode === 'POST' &&
    /^\/kassen\/[^/]+\/(belege|retouren|zahlungen|zahlungen\/[^/]+\/rueckerstattung|lieferdienste\/umsaetze|lieferdienste\/umsaetze\/[^/]+\/storno)$/.test(
      ohneAbfrage,
    ) &&
    !/^\/kassen\/[^/]+\/zahlungen\/vorgang\//.test(ohneAbfrage)
  );
}

/** N1: Verkaufsstopp (z. B. blockiertes Speicher-Upgrade) – kein Kassierweg wird gesendet. */
export class VerkaufGestoppt extends Error {}

/** Zeitlimit-Art eines Aufrufs (Pfad ohne Basis-URL). */
export function zeitlimitArt(methode: string, pfad: string): keyof Zeitlimits {
  const ohneAbfrage = pfad.split('?')[0] ?? pfad;
  const kasse = /^\/kassen\/[^/]+(\/.*)?$/.exec(ohneAbfrage);
  if (kasse === null) {
    if (ohneAbfrage === '/gesundheit') {
      return 'kurz';
    }
    // Kasse anlegen, Artikelstamm des Unternehmers ersetzen
    return methode === 'GET' ? 'standard' : 'gross';
  }
  const rest = kasse[1] ?? '';
  if (methode === 'POST' && (rest === '/belege' || rest === '/retouren')) {
    return 'beleg';
  }
  if (
    (methode === 'POST' && rest === '/zahlungen') ||
    (methode === 'POST' && /^\/zahlungen\/[^/]+\/(storno|rueckerstattung|klaerung)$/.test(rest))
  ) {
    return 'zahlung';
  }
  if (
    rest === '/status' ||
    rest === '/syncstand' ||
    rest === '/geraetestatus' ||
    rest === '/artikelstamm/pruefsumme' ||
    (methode === 'GET' && rest.startsWith('/zahlungen'))
  ) {
    return 'kurz';
  }
  if (
    rest === '/offline-synchronisation' ||
    rest === '/artikelstamm' ||
    rest.startsWith('/ausfall') ||
    rest === '/ausserbetriebnahme' ||
    rest === '/tagesabschluss' ||
    rest.startsWith('/berichte') ||
    rest === '/kassenbuch' ||
    rest === '/kassenbuch/wiederholung' ||
    rest.endsWith('/email') ||
    (methode === 'GET' && rest === '/belege')
  ) {
    return 'gross';
  }
  return 'standard';
}

export type Zahlungsstatus = 'ausstehend' | 'bezahlt' | 'abgelehnt' | 'storniert' | 'erstattet';

export interface Zahlung {
  zahlungsreferenz: string;
  kassenId: string;
  anbieter: string;
  betragCent: number;
  /** Im Betrag enthaltenes Trinkgeld (Karte). */
  trinkgeldCent?: number;
  status: Zahlungsstatus;
  details: Record<string, unknown>;
}

export interface KasseStatus {
  kassenId: string;
  status: string | null;
  unternehmerId: string | null;
  unternehmer: { name: string; anschrift: string; uid?: string } | null;
  zahlungsterminal: string | null;
  zahlungsanbieter: string | null;
  bridgeZugelassen: boolean;
  /**
   * S-1/F6: nur `true`, wenn der Server die nativen Druckrouten anbietet (BILANX_NATIVDRUCK=1,
   * Register 0095 geprüft). Fehlt bei ausgeschaltetem Schalter und bei älteren Servern.
   */
  nativdruck?: boolean;
  /** Branche des Betriebs (null = ohne Betriebsdatensatz, ältere API: fehlt). */
  branche?: string | null;
  /** Gebuchte Module; mit `leseModule` lesen. */
  module?: unknown;
  letzterBeleg: {
    belegnummer: string;
    belegtyp: string;
    sonderbeleg: string | null;
    zeitpunkt: string;
    umsatzzaehlerNachher: number;
    signaturGestoert: boolean;
  } | null;
  ausfallAktiv: boolean;
  /** Schlussbeleg der Außerbetriebnahme, sonst null. */
  schlussbeleg: { belegnummer: string; zeitpunkt: string } | null;
  pflichtbelege: { monatsbelegFehltFuer: string | null; jahresbelegFehltFuer: string | null };
  /** Letzter Tagesabschluss; `aktuell` = seither kein Beleg mehr. */
  tagesabschluss: {
    datum: string;
    erstelltAm: string;
    bediener: string | null;
    aktuell: boolean;
  } | null;
  signaturen: {
    zertifikatSeriennummerHex: string;
    jahr: string;
    produkt: string | null;
    limit: number | null;
    verbraucht: number;
    verbleibend: number | null;
    stufe: string;
  } | null;
}

/** Zeile des Belegjournals (Suche/letzte Belege). */
export interface JournalBeleg {
  belegnummer: string;
  belegtyp: string;
  sonderbeleg: string | null;
  zeitpunkt: string;
  summeCent: number;
  zahlungsart: 'bar' | 'karte' | 'gutschein' | null;
  signaturGestoert: boolean;
  notiz: string | null;
  bediener: string | null;
}

export interface BelegAntwort {
  id: number;
  kassenId: string;
  belegnummer: string;
  belegtyp: string;
  sonderbeleg: string | null;
  zeitpunkt: string;
  umsatzzaehlerNachher: number;
  signaturGestoert: boolean;
  jwsKompakt: string;
  qrCode: string;
}

export interface Geraeteauftrag {
  auftragId: string;
  kassenId: string;
  art: 'tagesabschluss' | 'druck';
  status: 'offen' | 'uebernommen' | 'erledigt' | 'fehlgeschlagen';
  details: Record<string, unknown>;
}

/** Artikelstamm vom Server (nur aktive Einträge für die Kasse). */
export interface Artikelstamm {
  unternehmerId: string;
  version: number;
  aktualisiertAm: string;
  /** `aktiv` fehlt in der Kassen-Antwort (dort nur aktive); die Betreiber-Antwort enthält auch deaktivierte. */
  gruppen: {
    id: number;
    name: string;
    farbe: string;
    icon: string | null;
    aktiv?: boolean;
    /** Produktionsstelle für Küchenbons; fehlt bei älterer API. */
    bonierziel?: string | null;
    /** USt-Vorschlag für neue Artikel der Gruppe; fehlt bei älterer API. */
    steuersatz?: string | null;
    /** Änderungscursor (P7); fehlt bei älterer API. */
    syncStand?: number;
  }[];
  artikel: {
    id: number;
    gruppeId: number;
    aktiv?: boolean;
    bezeichnung: string;
    bruttoCent: number;
    steuersatz: string;
    steuersatzAusserHaus: string | null;
    farbe: string | null;
    icon: string | null;
    ean?: string | null;
    /** Ab Migration 0050 (Artikelimport); fehlen bei älterer API. */
    artikelnummer?: string | null;
    barcode?: string | null;
    /** Gesperrt: an der Kasse sichtbar, aber nicht verkaufbar. */
    verkaufssperre?: boolean;
    verkaufssperreGrund?: string | null;
    /** Verkaufsart (Migration 0090): 'gewicht' = Wiegeartikel, `bruttoCent` ist der Kilopreis; fehlt = 'stueck'. */
    verkaufsart?: 'stueck' | 'gewicht';
    /** Änderungscursor (P7); fehlt bei älterer API. */
    syncStand?: number;
  }[];
}

/** Delta-Antwort (P7): `vollstaendig` = alle Zeilen inkl. deaktivierter, sonst Änderungen seit `seit`. */
export interface ArtikelstammDelta extends Artikelstamm {
  syncStand: number;
  seit: number;
  vollstaendig: boolean;
}

export interface Syncstand {
  kassenId: string;
  kassenStatus: string;
  serverstart: string;
  zeit: string;
  stamm: { version: number; syncStand: number } | null;
  module: string[] | null;
  modulStand: string | null;
  /** P10: Profil der Kasse (fehlt bei älterer API). */
  schnellwahl?: { profilId: number; version: number } | null;
}

export interface StammPruefsumme {
  version: number;
  syncStand: number;
  gruppen: number;
  artikel: number;
  sha256: string;
}

/** Vollabgleich: weggelassene optionale Felder bleiben am Server unverändert, null löscht. */
export interface ArtikelstammEingabe {
  /** Bekannter Datenstand des Servers (0 = noch kein Stamm); Pflicht (P2), sonst 428. */
  version?: number;
  gruppen: { id?: number; name: string; farbe: string; icon?: string | null }[];
  artikel: {
    id?: number;
    gruppe: string;
    bezeichnung: string;
    bruttoCent: number;
    steuersatz: string;
    steuersatzAusserHaus?: string | null;
    farbe?: string | null;
    icon?: string | null;
    ean?: string | null;
  }[];
}

export interface Empfaenger {
  name: string;
  anschrift: string;
  uid?: string;
}

/** Bediener aus der Verwaltung am Server (Backoffice); PIN wird nie übertragen. */
export interface BedienerKurz {
  id: number;
  name: string;
  pinNoetig: boolean;
  rechte: { storno: boolean; rabatt: boolean; tagesabschluss: boolean };
}

/** Kunde aus dem Kundenstamm des Unternehmers (nur die Felder für den Beleg). */
export interface KundeKurz {
  id: number;
  name: string;
  anschrift: string;
  uid: string | null;
  email: string | null;
  /** Fester Kundenrabatt in Prozent; fehlt bei älterer API. */
  rabattProzent?: number | null;
}

export interface BelegDetails extends BelegAntwort {
  /** Trinkgeld zum Beleg (kein Umsatz), falls vorhanden. */
  trinkgeldCent?: number;
  betraege: Record<string, number>;
  positionen: Position[];
  zahlungsart: 'bar' | 'karte' | 'gutschein' | null;
  zahlungsreferenz: string | null;
  empfaenger: Empfaenger | null;
  notiz: string | null;
  bediener: string | null;
  ocrCode: string;
  /** Mit Mehrzweck-Gutscheinen bezahlter Teil der Belegsumme. */
  gutscheinCent: number;
  gutscheine: { code: string; art: string; betragCent: number; restNachherCent: number }[];
  /** Ob zu diesem Beleg ein Rechnungsdokument (§ 11 UStG) möglich ist; fehlt bei älteren Servern. */
  rechnung?: {
    moeglich: boolean;
    art?: 'kleinbetrag' | 'vollstaendig';
    grund?: string;
    /** Pflichthinweise auf der Rechnung (Steuerbefreiung, Kleinunternehmer). */
    hinweise?: string[];
  };
}

export type KassenbuchArt = 'einlage' | 'entnahme' | 'zaehlung';

export interface KassenbuchEintrag {
  id: number;
  art: KassenbuchArt;
  betragCent: number;
  sollCent: number | null;
  differenzCent: number | null;
  grund: string | null;
  erstelltAm: string;
}

export interface Bargeldbestand {
  sollCent: number;
  letzteZaehlung: { zeitpunkt: string; istCent: number } | null;
  einlagenCent: number;
  entnahmenCent: number;
  barumsatzCent: number;
}

export interface Kassenbuch {
  kassenId: string;
  datum: string;
  bestand: Bargeldbestand;
  eintraege: KassenbuchEintrag[];
  /** E-W1-RKSV F2: nur Server ab 17f2d82 melden `kassenbuchVorgang: true` (Wiederholung erkannt). */
  faehigkeiten?: { kassenbuchVorgang?: boolean };
}

export interface Tagesbericht {
  kassenId: string;
  datum: string;
  zeitraum: { von: string; bis: string };
  anzahl: {
    gesamt: number;
    standard: number;
    storno: number;
    training: number;
    nullbelege: number;
    start: number;
    ausfallbelege: number;
  };
  umsatz: {
    bruttoCent: number;
    verkauftCent: number;
    storniertCent: number;
    nachZahlungsart: Record<'bar' | 'karte' | 'gutschein', { anzahl: number; bruttoCent: number }>;
    betraege: Record<string, number>;
    nachSteuersatz: {
      steuersatz: string;
      bruttoCent: number;
      nettoCent: number;
      ustCent: number;
    }[];
  };
  trainingBruttoCent: number;
  /** Trinkgeld (Karte, kein Umsatz), Storno negativ. */
  trinkgeldCent: number;
  umsatzzaehler: { anfangCent: number; endeCent: number } | null;
  bargeld: {
    einlagenCent: number;
    entnahmenCent: number;
    zaehlungen: {
      zeitpunkt: string;
      istCent: number;
      sollCent: number | null;
      differenzCent: number | null;
    }[];
  };
  nachBediener: {
    bediener: string;
    standard: number;
    storno: number;
    bruttoCent: number;
    trinkgeldCent: number;
  }[];
  ersterBeleg: { belegnummer: string; zeitpunkt: string } | null;
  letzterBeleg: { belegnummer: string; zeitpunkt: string } | null;
  /** Online bezahlte Lieferdienst-Bestellungen (kein Barumsatz); fehlt ohne Modul. */
  plattformen?: PlattformSumme[];
}

export type UmsatzPlattform = 'lieferando' | 'foodora' | 'webshop';

export const PLATTFORM_NAME: Record<UmsatzPlattform, string> = {
  lieferando: 'Lieferando',
  foodora: 'Foodora',
  webshop: 'Webshop',
};

export interface PlattformSumme {
  plattform: UmsatzPlattform;
  anzahl: number;
  stornos: number;
  bruttoCent: number;
  nachSteuersatz: { steuersatz: string; bruttoCent: number; nettoCent: number; ustCent: number }[];
}

/** Online bezahlte Lieferdienst-Bestellung (kein Registrierkassenbeleg). */
export interface Plattformumsatz {
  id: number;
  plattform: UmsatzPlattform;
  externeId: string;
  lieferart: 'lieferung' | 'abholung';
  positionen: Position[];
  summeCent: number;
  bediener: string | null;
  notiz: string | null;
  stornoVon: number | null;
  zeitpunkt: string;
}

/** Protokollierter Tagesabschluss (append-only) mit Tagesbericht-Momentaufnahme. */
export interface Tagesabschluss {
  id: number;
  kassenId: string;
  datum: string;
  bediener: string | null;
  zaehlungId: number | null;
  terminalAuftragId: string | null;
  bericht: Tagesbericht;
  erstelltAm: string;
}

export interface Gutschein {
  unternehmerId: string;
  code: string;
  art: 'mehrzweck' | 'einzweck';
  steuersatz: Steuersatz | null;
  bezeichnung: string | null;
  gueltigBis: string | null;
  nennwertCent: number;
  restCent: number;
  gesperrt: boolean;
  ausgegebenAm: string;
  belegnummerAusgabe: string | null;
}

/** Geparkter Bon am Server (offener Tisch). */
export interface OffenerBon {
  name: string;
  kassenId: string;
  bediener: string | null;
  positionen: Position[];
  empfaenger: Empfaenger | null;
  notiz: string | null;
  /** Bonierstand (Küchenbons); fehlt bei älterer API. */
  boniert?: BonierStand[];
  version: number;
  geparktAm: string;
}

export type TerminStatus = 'geplant' | 'erschienen' | 'abgesagt' | 'nicht_erschienen';

export interface Termin {
  id: number;
  /** ISO-8601 (UTC). */
  beginn: string;
  dauerMin: number;
  kunde: string;
  telefon: string | null;
  leistung: string | null;
  artikelId: number | null;
  bediener: string | null;
  notiz: string | null;
  status: TerminStatus;
}

export type TerminEingabe = Omit<Termin, 'id'>;

export interface Tischplan {
  bereiche: {
    name: string;
    tische: {
      name: string;
      x: number;
      y: number;
      b: number;
      h: number;
      form: 'eckig' | 'rund';
      plaetze: number | null;
    }[];
  }[];
}

export interface Bonierung {
  id: number;
  kassenId: string;
  ziel: string;
  tisch: string | null;
  bediener: string | null;
  notiz: string | null;
  zeilen: { bezeichnung: string; menge: number }[];
  erledigtAm: string | null;
  erstelltAm: string;
}

export interface BelegAuftrag {
  belegtyp: 'START' | 'STANDARD' | 'STORNO' | 'NULL' | 'TRAINING';
  belegnummer: string;
  /** Kunde aus dem Kundenstamm, dessen Rabatt auf dem Bon steht (Prüfung am Server, S3). */
  kundeId?: number;
  positionen?: Position[];
  betraege?: Record<string, number>;
  zahlungsart?: 'bar' | 'karte' | 'gutschein';
  zahlungsreferenz?: string;
  /** Eingelöste Gutscheine (Code, Betrag in Cent). */
  gutscheine?: { code: string; betragCent: number }[];
  /** Mit diesem Beleg verkaufte Gutscheine. */
  gutscheinAusgaben?: {
    code: string;
    art: 'mehrzweck' | 'einzweck';
    nennwertCent: number;
    steuersatz?: Steuersatz;
    bezeichnung?: string;
    gueltigBis?: string;
  }[];
  /** Storno: stornierter Beleg – dessen Gutscheinbewegungen werden zurückgebucht. */
  stornoVon?: string;
  sonderbeleg?: 'MONATSBELEG' | 'JAHRESBELEG' | 'SCHLUSSBELEG' | 'SAMMELBELEG';
  /** Rechnungsempfänger (§ 11 UStG): nur bei ausdrücklich verlangter Rechnung. */
  empfaenger?: Empfaenger;
  /** Freitext auf dem Beleg (max. 200 Zeichen). */
  notiz?: string;
  /** Kassierkraft (Anzeigename, max. 60 Zeichen). */
  bediener?: string;
  /** Signierte Bediener-Anmeldung (Storno mit Bedienerverwaltung). */
  bedienerSitzung?: string;
  /** Bar gegebener Betrag in Cent – steht als Gegeben/Rückgeld auf dem Bon. */
  gegebenCent?: number;
  /** Trinkgeld in Cent (kein Umsatz; Teil der Vorgangs-Nutzdaten). */
  trinkgeldCent?: number;
  /**
   * N1: dauerhafte Vorgangskennung des Verkaufs (vor dem ersten Sendeversuch gespeichert). Eine
   * Wiederholung mit derselben ID und demselben Inhalt liefert den vorhandenen Beleg.
   */
  vorgang?: Vorgang;
}

/** N1: Stand eines gesendeten Verkaufs am Server. */
export type VorgangsStand =
  | { stand: 'gebucht'; beleg: BelegAntwort }
  | { stand: 'verworfen'; grund: string }
  | { stand: 'klaerung'; klaerungen: number[] }
  | { stand: 'unbekannt' };

/** N1: Vorgangskennung eines Verkaufs – ID und ursprünglicher Verkaufszeitpunkt (ISO-8601). */
export interface Vorgang {
  id: string;
  zeitpunkt: string;
}

/**
 * N1-Versionszaun: Protokollversion dieser Kassen-App (Kopf X-Kassen-Version). Ab 2 vergibt sie
 * Nummern über den gemeinsamen Zähler und sendet Vorgangskennungen; ab 3 mit zentraler
 * Kassiersperre und Lieferdienst-Sperre (Doppelverkauf 25.10.2026). Ältere Fenster lehnt der
 * Server beim Kassieren ab.
 */
export const KASSEN_PROTOKOLLVERSION = 3;

/** Wareneingang/Inventur per Scan (Modul Lager). */
export interface ErfassungPosition {
  code: string;
  menge: number;
  bezeichnung?: string;
}

export interface Artikelvorschlag {
  id: number;
  ean: string;
  bezeichnung: string | null;
  menge: number;
  einstandspreisCent: number | null;
  quelle: string;
  bezug: string | null;
  status: 'offen' | 'uebernommen' | 'verworfen';
  erstelltAm: string;
}

export type ErfassungErgebnis =
  | {
      code: string;
      ergebnis: 'gebucht';
      artikelId: number;
      bezeichnung: string;
      menge: number;
      bestand: number;
      bestandsfuehrungEingeschaltet: boolean;
    }
  | {
      /** Zählung im Inventurlauf: noch kein Bestand – den setzt erst der Abschluss. */
      code: string;
      ergebnis: 'gezaehlt';
      artikelId: number;
      bezeichnung: string;
      menge: number;
      laufId: number;
    }
  | { code: string; ergebnis: 'vorschlag'; vorschlag: Artikelvorschlag }
  | { code: string; ergebnis: 'fehler'; fehler: string };

export interface CodeAuskunft {
  ean: string;
  artikel: {
    id: number;
    bezeichnung: string;
    bruttoCent: number;
    bestandsfuehrung: boolean;
    bestand: number | null;
  } | null;
  vorschlag: Artikelvorschlag | null;
  produkt: { bezeichnung: string } | null;
}

/** Antwort von GET /kassen/:id/lager/code/:code (gleich der Handterminal-Auskunft). */
export interface LagerCodeAuskunft {
  code: string;
  ean: string | null;
  status: 'gefunden' | 'mehrdeutig' | 'unbekannt';
  artikel: {
    id: number;
    bezeichnung: string;
    bestand: number | null;
    gesperrt?: boolean;
    sperrgrund?: string | null;
  } | null;
  mehrdeutig?: { id: number; bezeichnung: string; ean: string | null }[];
  vorschlag: { bezeichnung: string | null } | null;
  produkt: { bezeichnung: string } | null;
}

export interface VorschlagUebernahme {
  gruppe: string;
  bezeichnung: string;
  bruttoCent: number;
  steuersatz: string;
  einstandspreisCent?: number | null;
  /** Codex K-7: Vorgangskennung – Wiederholung bucht nicht doppelt (ältere Server ignorieren sie). */
  vorgangId?: string;
}

export interface NeueKasse {
  kassenId: string;
  unternehmerId: string;
  unternehmer: { name: string; anschrift: string; uid?: string };
  zahlungsterminal?: string;
  zahlungsanbieter?: 'sumup' | 'zvt' | 'dev';
  /** Branche: legt beim ersten Kassenanlegen die Module nach Vorlage fest. */
  branche?: 'gastro' | 'imbiss' | 'handel' | 'dienstleistung' | 'sonstige';
}

export const NULL_BETRAEGE = {
  normal: 0,
  ermaessigt1: 0,
  ermaessigt2: 0,
  nullsatz: 0,
  besonders: 0,
};

/** Server-Sent Events zeilenweise lesen (event/data, Leerzeile = Ende eines Ereignisses). */
export async function leseEreignisse(
  koerper: ReadableStream<Uint8Array>,
  beiEreignis: (art: string, daten: unknown) => void,
): Promise<void> {
  const leser = koerper.getReader();
  const dek = new TextDecoder();
  let puffer = '';
  let art = 'message';
  let daten = '';
  for (;;) {
    const teil = await leser.read();
    if (teil.done) {
      return;
    }
    puffer += dek.decode(teil.value, { stream: true });
    let i = puffer.indexOf('\n');
    while (i >= 0) {
      const zeile = puffer.slice(0, i).replace(/\r$/, '');
      puffer = puffer.slice(i + 1);
      if (zeile === '') {
        if (daten !== '' || art !== 'message') {
          let wert: unknown = daten;
          try {
            wert = JSON.parse(daten);
          } catch {
            // Text bleibt Text
          }
          beiEreignis(art, wert);
        }
        art = 'message';
        daten = '';
      } else if (zeile.startsWith('event:')) {
        art = zeile.slice(6).trim();
      } else if (zeile.startsWith('data:')) {
        daten += (daten === '' ? '' : '\n') + zeile.slice(5).trim();
      }
      i = puffer.indexOf('\n');
    }
  }
}

function istRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

/** Antwort der Zählroute eines Inventurlaufs (Kasse und Lagergerät). */
export interface ZaehlAntwort {
  laufId: number;
  ergebnisse: (
    | {
        index: number;
        ergebnis: 'gezaehlt';
        artikelId: number;
        bezeichnung: string;
        menge: number;
      }
    | { index: number; ergebnis: 'fehler'; code: string | null; fehler: string }
  )[];
  wiederholt?: boolean;
}

/** Körper für die Zählroute aus dem eingefrorenen Auftrag (immer gleich – Wiederholschutz). */
export function zaehlKoerper(k: {
  vorgangId: string;
  bezug?: string;
  positionen: readonly { code: string; menge: number }[];
  laufAnker?: { letzteLaufId: number; offenLaufId: number | null };
}): unknown {
  return {
    vorgangId: k.vorgangId,
    zaehlstelle: (k.bezug ?? '').trim(),
    positionen: k.positionen.map((p) => ({ code: p.code, menge: p.menge })),
    ...(k.laufAnker === undefined ? {} : { laufAnker: k.laufAnker }),
  };
}

/** Zählantwort im Format der Warteschlange: „gezaehlt“ (kein Bestand bis zum Abschluss) bzw. Fehler. */
export function zaehlAntwortAlsErfassung(
  r: ZaehlAntwort,
  positionen: readonly { code: string }[],
  laufId: number,
): { ergebnisse: ErfassungErgebnis[]; wiederholt?: boolean } {
  const ergebnisse: ErfassungErgebnis[] = r.ergebnisse.map((e) => {
    const code = positionen[e.index]?.code ?? '';
    return e.ergebnis === 'gezaehlt'
      ? {
          code,
          ergebnis: 'gezaehlt',
          artikelId: e.artikelId,
          bezeichnung: e.bezeichnung,
          menge: e.menge,
          laufId,
        }
      : { code: e.code ?? code, ergebnis: 'fehler', fehler: e.fehler };
  });
  return { ergebnisse, ...(r.wiederholt === true ? { wiederholt: true } : {}) };
}

/**
 * Fähigkeiten dieser Kassen-App, die der Server bei Beleg, Offline-Nachsendung und Zahlungsstart
 * mitbekommt: 'gewicht' = kann Wiegepositionen (einheit 'kg') erzeugen und stornieren. Ohne die
 * Angabe lehnt der Server kg-Positionen ab (422 bzw. Offline „fehler“). Nicht Teil des
 * Vorgangs-Hashs – ältere Puffer-Einträge bleiben wiederholbar.
 */
export const KASSEN_FAEHIGKEITEN: readonly string[] = ['gewicht'];

/** Antwort der Offline-Synchronisation. */
export interface OfflineSyncAntwort {
  belege: {
    belegnummer: string;
    /**
     * N1: `klaerung` = Nummer vergeben, nicht sicher derselbe Vorgang – Eintrag bleibt, bis im
     * Backoffice entschieden ist. `konflikt` nur von älteren Servern.
     */
    status: 'erstellt' | 'vorhanden' | 'konflikt' | 'klaerung' | 'fehler';
    fehler?: string;
    /** Rechnungswunsch nicht erfüllbar: als Kassenbeleg ohne Empfänger signiert. */
    hinweis?: string;
    /** N1: Klärungsfall. */
    klaerung?: number;
  }[];
  sammelbeleg: { belegnummer: string } | null;
  /** R-12 Ü-2 (Codex R12-3): jüngster Beleg der Kasse, sobald ein Eintrag quittiert ist. */
  geraetezeuge?: { id: number; belegnummer: string; jwsKompakt: string; zeugeMac?: string };
  ausfallBeendet: boolean;
  /** Aktiver Ausfall der Kasse (null = keiner). */
  ausfall?: unknown;
  /** N1: offene Klärungsfälle der Kasse halten den Ausfall offen. */
  klaerungenOffen?: number;
  /** N1 (Codex-Runde 13): Nachsendelauf vollständig abgeschlossen (nächster Lauf: neue Kennung). */
  laufAbgeschlossen?: boolean;
}

export class KassenApi {
  private readonly basisUrl: string;
  private readonly fetchFn: typeof fetch;

  private readonly token: () => string | null;
  private readonly bedienerSitzung: () => string | null;
  private readonly anmeldung: AnmeldeWaechter | undefined;

  constructor(opt: {
    basisUrl: string;
    fetchFn?: typeof fetch;
    token?: () => string | null;
    /** Signierte Bediener-Anmeldung: der Server prüft damit Rechte (z. B. Abschlussrecht). */
    bedienerSitzung?: () => string | null;
    /** Hält Abrufe mit ungültigem Kassenschlüssel bzw. während einer Adresssperre an. */
    anmeldung?: AnmeldeWaechter;
    /** Server meldet veraltete Preise (P8/S-5) – z. B. Abgleich des Stamms anstoßen. */
    beiPreisVeraltet?: () => void;
    /** P1-5: abweichende Zeitlimits (Tests). */
    zeitlimits?: Partial<Zeitlimits>;
    /**
     * N1: aktueller Verkaufsstopp (Text) oder null. Gilt für jeden Kassierweg dieses Fensters –
     * unabhängig davon, welche Schaltfläche ihn auslöst.
     */
    verkaufsstopp?: () => string | null;
    /** R-12 Ü-2: dauerhafter Gerätezeuge (zuletzt bestätigter Beleg je Kasse). */
    zeuge?: GeraetezeugeAblage;
  }) {
    this.basisUrl = opt.basisUrl.replace(/\/+$/, '');
    this.fetchFn = opt.fetchFn ?? ((eingabe, init) => fetch(eingabe, init));
    this.zeitlimits = { ...ZEITLIMITS, ...opt.zeitlimits };
    this.token = opt.token ?? (() => null);
    this.bedienerSitzung = opt.bedienerSitzung ?? (() => null);
    this.anmeldung = opt.anmeldung;
    this.beiPreisVeraltet = opt.beiPreisVeraltet;
    this.verkaufsstopp = opt.verkaufsstopp ?? (() => null);
    this.zeuge = opt.zeuge;
  }

  private readonly zeuge: GeraetezeugeAblage | undefined;

  /**
   * av5 V-8: Das Lesen des Zeugen ist zuletzt gescheitert (Gerätespeicher). Ein 426 des Servers
   * heißt dann nicht „App zu alt“, sondern „Gerätespeicher nicht lesbar“.
   */
  private zeugeNichtLesbar = false;

  /**
   * R-12 Ü-2: Feld `geraetezeuge` für Beleg und Synchronisation. Ohne Ablage bzw. wenn das Lesen
   * scheitert, fehlt das Feld – der Server (Pflicht) weist dann ab, statt ungeprüft zu signieren.
   */
  private async zeugeFeld(kassenId: string): Promise<{ geraetezeuge?: Geraetezeuge | null }> {
    if (this.zeuge === undefined) {
      return {};
    }
    try {
      const geraetezeuge = await this.zeuge.lese(kassenId);
      this.zeugeNichtLesbar = false;
      return { geraetezeuge };
    } catch (e: unknown) {
      console.error('Gerätezeuge nicht lesbar:', e);
      this.zeugeNichtLesbar = true;
      return {};
    }
  }

  /**
   * Bestätigten Beleg merken; scheitert das, bleibt der ältere Zeuge (der Server kennt ihn).
   * `streng` (Synchronisation, N-4): dann `ZeugeNichtGespeichert` – der Puffer bleibt.
   */
  private async zeugeMerken(kassenId: string, beleg: unknown, streng = false): Promise<void> {
    if (
      this.zeuge === undefined ||
      !istRecord(beleg) ||
      typeof beleg.belegnummer !== 'string' ||
      typeof beleg.jwsKompakt !== 'string'
    ) {
      return;
    }
    try {
      await this.zeuge.merke(kassenId, {
        belegnummer: beleg.belegnummer,
        jwsKompakt: beleg.jwsKompakt,
        ...(typeof beleg.id === 'number' ? { id: beleg.id } : {}),
        ...(typeof beleg.zeugeMac === 'string' ? { mac: beleg.zeugeMac } : {}),
      });
    } catch (e: unknown) {
      if (streng) {
        throw new ZeugeNichtGespeichert(e);
      }
      console.error('Gerätezeuge nicht gespeichert:', e);
    }
  }

  private readonly verkaufsstopp: () => string | null;

  private readonly beiPreisVeraltet: (() => void) | undefined;
  private readonly zeitlimits: Zeitlimits;

  /**
   * P1-5: Abruf samt Lesen des Körpers mit Zeitlimit. Läuft es ab, wird die Anfrage
   * abgebrochen und mit ApiFehler(0) beendet – auch wenn die Gegenstelle das Signal nicht
   * beachtet (Promise-Wettlauf).
   */
  private async mitZeitlimit<R>(
    methode: string,
    pfad: string,
    arbeit: (signal: AbortSignal) => Promise<R>,
  ): Promise<R> {
    const ms = this.zeitlimits[zeitlimitArt(methode, pfad)];
    const abbruch = new AbortController();
    let uhr: ReturnType<typeof setTimeout> | undefined;
    const ablauf = new Promise<never>((_loese, lehneAb) => {
      uhr = setTimeout(() => {
        lehneAb(new ApiZeitueberschreitung());
        abbruch.abort();
      }, ms);
    });
    try {
      return await Promise.race([arbeit(abbruch.signal), ablauf]);
    } finally {
      clearTimeout(uhr);
    }
  }

  private async aufruf<T>(
    methode: 'GET' | 'POST' | 'PUT' | 'DELETE',
    pfad: string,
    body?: unknown,
    alsText = false,
  ): Promise<T> {
    const stopp = istKassierweg(methode, pfad) ? this.verkaufsstopp() : null;
    if (stopp !== null) {
      // Kein Netzfehler: nie puffern, nichts senden
      throw new VerkaufGestoppt(stopp);
    }
    const token = this.token();
    const sitzung = this.bedienerSitzung();
    // Ungültiger Schlüssel bzw. Adresssperre: nicht erneut anfragen (jeder Versuch zählte als
    // Fehlversuch am Server), sondern sofort mit dem Grund abbrechen.
    const gesperrt = this.anmeldung?.sperre(token) ?? null;
    if (gesperrt !== null) {
      throw new ApiFehler(gesperrt.status, gesperrt.meldung);
    }
    const { res, text } = await this.mitZeitlimit(methode, pfad, async (signal) => {
      const antwort = await this.fetchFn(`${this.basisUrl}${pfad}`, {
        method: methode,
        headers: {
          Accept: alsText ? 'text/plain' : 'application/json',
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
          ...(sitzung === null ? {} : { 'X-Bediener-Sitzung': sitzung }),
          'X-Kassen-Version': String(KASSEN_PROTOKOLLVERSION),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal,
      });
      return { res: antwort, text: await antwort.text() };
    });
    if (!res.ok) {
      let fehler = text;
      let json: unknown = null;
      try {
        json = JSON.parse(text);
        if (istRecord(json) && typeof json.fehler === 'string') {
          fehler = json.fehler;
        }
      } catch {
        // kein JSON – Text behalten
      }
      // Nur werten, wenn die Antwort zum aktuell gespeicherten Schlüssel gehört (nicht zu einem
      // inzwischen ersetzten).
      if ((res.status === 401 || res.status === 429) && token === this.token()) {
        const warte = Number(res.headers.get('Retry-After'));
        this.anmeldung?.melde(
          res.status,
          token,
          istRecord(json) ? json : {},
          Number.isFinite(warte) && warte > 0 ? warte : null,
        );
      }
      if (res.status === 409 && istRecord(json) && Array.isArray(json.preisVeraltet)) {
        this.beiPreisVeraltet?.();
      }
      if (res.status === 422 && istRecord(json)) {
        fehler = formFehlerMeldung(json) ?? fehler;
      }
      if (
        res.status === 426 &&
        this.zeugeNichtLesbar &&
        istRecord(json) &&
        json.code === 'app_update_erforderlich'
      ) {
        // V-8: nicht die App ist zu alt, sondern der Gerätespeicher war nicht lesbar
        throw new ApiFehler(426, GERAETESPEICHER_NICHT_LESBAR, {
          ...json,
          geraetespeicherNichtLesbar: true,
        });
      }
      throw new ApiFehler(
        res.status,
        fehler || `HTTP ${String(res.status)}`,
        istRecord(json) ? json : null,
      );
    }
    return (alsText ? text : (JSON.parse(text) as unknown)) as T;
  }

  /**
   * Rohabruf mit bedingtem ETag (P7): 304 ist kein Fehler. Gleiche Anmeldesperre und
   * Fehlerauswertung wie `aufruf`.
   */
  private async bedingt(
    pfad: string,
    etag: string | null,
  ): Promise<{ status: 304 } | { status: 200; etag: string | null; daten: unknown }> {
    const token = this.token();
    const gesperrt = this.anmeldung?.sperre(token) ?? null;
    if (gesperrt !== null) {
      throw new ApiFehler(gesperrt.status, gesperrt.meldung);
    }
    const { res, text } = await this.mitZeitlimit('GET', pfad, async (signal) => {
      const antwort = await this.fetchFn(`${this.basisUrl}${pfad}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
          ...(etag === null ? {} : { 'If-None-Match': etag }),
        },
        signal,
      });
      return { res: antwort, text: antwort.status === 304 ? '' : await antwort.text() };
    });
    if (res.status === 304) {
      return { status: 304 };
    }
    if (!res.ok) {
      let json: unknown = null;
      try {
        json = JSON.parse(text);
      } catch {
        // kein JSON
      }
      if ((res.status === 401 || res.status === 429) && token === this.token()) {
        const warte = Number(res.headers.get('Retry-After'));
        this.anmeldung?.melde(
          res.status,
          token,
          istRecord(json) ? json : {},
          Number.isFinite(warte) && warte > 0 ? warte : null,
        );
      }
      const fehler = istRecord(json) && typeof json.fehler === 'string' ? json.fehler : text;
      throw new ApiFehler(res.status, fehler || `HTTP ${String(res.status)}`);
    }
    return { status: 200, etag: res.headers.get('ETag'), daten: JSON.parse(text) as unknown };
  }

  gesundheit(): Promise<{ status: string }> {
    return this.aufruf('GET', '/gesundheit');
  }

  status(kassenId: string): Promise<KasseStatus> {
    return this.aufruf('GET', `/kassen/${encodeURIComponent(kassenId)}/status`);
  }

  /** Gescannten Code nachschlagen: Artikel mit Bestand oder offener Vorschlag. */
  codeAuskunft(kassenId: string, code: string): Promise<CodeAuskunft> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/lager/artikel/${encodeURIComponent(code)}`,
    );
  }

  /**
   * Lager-Code-Auskunft (gemeinsame Zuordnung mit dem Handterminal, lagercode.ts): Text-Codes,
   * exakt Barcode → EAN → Artikelnummer, danach EAN-Normalisierung; mit Bestand und Sperrgrund.
   */
  lagerCodeAuskunft(kassenId: string, code: string): Promise<LagerCodeAuskunft> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/lager/code/${encodeURIComponent(code)}`,
    );
  }

  /** Wareneingang oder Inventur buchen; Ergebnis je Position. */
  /** Mit `vorgangId` bucht eine Wiederholung (Antwort verloren) nicht doppelt. */
  lagerErfassung(
    kassenId: string,
    art: 'wareneingang' | 'inventur',
    positionen: ErfassungPosition[],
    bezug?: string,
    vorgangId?: string,
    /** Inventur: Laufanker der Liste (der Server bucht nie über einen Inventurlauf hinweg). */
    laufAnker?: { letzteLaufId: number; offenLaufId: number | null },
  ): Promise<{ ergebnisse: ErfassungErgebnis[] }> {
    return this.aufruf('POST', `/kassen/${encodeURIComponent(kassenId)}/lager/erfassung`, {
      art,
      positionen,
      ...(bezug === undefined || bezug === '' ? {} : { bezug }),
      ...(vorgangId === undefined ? {} : { vorgangId }),
      ...(laufAnker === undefined ? {} : { laufAnker }),
    });
  }

  /** Laufstand des Unternehmers der Kasse: offener Inventurlauf und höchste Lauf-ID. */
  async inventurStand(kassenId: string): Promise<{
    letzteLaufId: number;
    offen: { id: number; bezeichnung: string } | null;
  }> {
    const r = await this.aufruf<{
      lauf: { id: number; bezeichnung: string } | null;
      letzteLaufId?: number;
    }>('GET', `/kassen/${encodeURIComponent(kassenId)}/lager/inventurlaeufe/offen`);
    if (r.letzteLaufId === undefined) {
      // ältere API ohne höchste Lauf-ID: Stand unbekannt (keine Inventur übergeben)
      throw new ApiFehler(0, 'Laufstand vom Server unbekannt – App bzw. Server aktualisieren');
    }
    return {
      letzteLaufId: r.letzteLaufId,
      offen: r.lauf === null ? null : { id: r.lauf.id, bezeichnung: r.lauf.bezeichnung },
    };
  }

  /** Zählung einer Inventurliste im offenen Lauf (Zählstelle = bezug, gleiche Vorgangskennung). */
  async inventurZaehlung(
    kassenId: string,
    laufId: number,
    koerper: {
      vorgangId: string;
      bezug?: string;
      positionen: { code: string; menge: number }[];
      laufAnker?: { letzteLaufId: number; offenLaufId: number | null };
    },
  ): Promise<{ ergebnisse: ErfassungErgebnis[]; wiederholt?: boolean }> {
    const r = await this.aufruf<ZaehlAntwort>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/lager/inventurlaeufe/${String(laufId)}/zaehlungen`,
      zaehlKoerper(koerper),
    );
    return zaehlAntwortAlsErfassung(r, koerper.positionen, laufId);
  }

  artikelvorschlaege(kassenId: string): Promise<{
    vorschlaege: Artikelvorschlag[];
    gruppen: string[];
    /** USt-Vorschlag je Gruppenname (fehlt bei älterer API). */
    gruppenSteuersatz?: Record<string, string | null>;
  }> {
    return this.aufruf('GET', `/kassen/${encodeURIComponent(kassenId)}/lager/vorschlaege`);
  }

  vorschlagUebernehmen(
    kassenId: string,
    id: number,
    daten: VorschlagUebernahme,
  ): Promise<{ bestand: number }> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/lager/vorschlaege/${String(id)}/uebernahme`,
      daten,
    );
  }

  vorschlagVerwerfen(kassenId: string, id: number): Promise<unknown> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/lager/vorschlaege/${String(id)}/verwerfen`,
    );
  }

  kasseAnlegen(kasse: NeueKasse): Promise<{ kassenId: string }> {
    return this.aufruf('POST', '/kassen', kasse);
  }

  /**
   * E-W1-RKSV F2 (Rollback-Untergrenze technisch): Erkennt der Server Wiederholungen derselben
   * Vorgangskennung? Fehler bzw. fehlendes Feld (älterer Server) gelten als „nein“.
   */
  async kassenbuchErkenntWiederholung(kassenId: string, datum: string): Promise<boolean> {
    try {
      const k = await this.kassenbuch(kassenId, datum);
      return k.faehigkeiten?.kassenbuchVorgang === true;
    } catch {
      return false;
    }
  }

  kassenbuch(kassenId: string, datum: string): Promise<Kassenbuch> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/kassenbuch?datum=${encodeURIComponent(datum)}`,
    );
  }

  /** Tagesabschluss protokollieren (Server hält den Tagesbericht als Momentaufnahme fest). */
  tagesabschlussProtokollieren(
    kassenId: string,
    eingabe: { datum?: string; bediener?: string; zaehlungId?: number; terminalAuftragId?: string },
  ): Promise<{ abschluss: Tagesabschluss; email: { an: string[]; fehler?: string } | null }> {
    return this.aufruf('POST', `/kassen/${encodeURIComponent(kassenId)}/tagesabschluss`, eingabe);
  }

  tagesabschluesse(
    kassenId: string,
    von: string,
    bis: string,
  ): Promise<{ abschluesse: Tagesabschluss[] }> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/tagesabschluss?von=${encodeURIComponent(von)}&bis=${encodeURIComponent(bis)}`,
    );
  }

  kassenbuchBuchen(
    kassenId: string,
    eintrag: {
      art: KassenbuchArt;
      betragCent: number;
      grund?: string;
      /**
       * A5-M7: Vorgangskennung der Buchung – eine Wiederholung nach verlorener Antwort trägt
       * dieselbe. Server ohne Vorgangsprüfung ignorieren das Feld (Schema entfernt Unbekanntes).
       */
      vorgangId?: string;
    },
  ): Promise<{ eintrag: KassenbuchEintrag; bestand: Bargeldbestand }> {
    return this.aufruf('POST', `/kassen/${encodeURIComponent(kassenId)}/kassenbuch`, eintrag);
  }

  /**
   * E-W1-RKSV F2: Wiederholung einer ungeklärten Kassenbuch-Buchung (dieselbe Kennung, derselbe
   * Inhalt). Eigene Route, die nur Server mit Vorgangsprüfung (17f2d82) kennen – ein älterer Server
   * antwortet 404 statt erneut zu buchen.
   */
  kassenbuchWiederholen(
    kassenId: string,
    eintrag: { art: KassenbuchArt; betragCent: number; grund?: string; vorgangId: string },
  ): Promise<{ eintrag: KassenbuchEintrag; bestand: Bargeldbestand }> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/kassenbuch/wiederholung`,
      eintrag,
    );
  }

  /** Offene Tische (geparkte Bons) des Unternehmers am Server. */
  tische(kassenId: string): Promise<OffenerBon[]> {
    return this.aufruf<{ tische: OffenerBon[] }>(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/tische`,
    ).then((r) => r.tische);
  }

  tischParken(
    kassenId: string,
    name: string,
    bon: {
      positionen: Position[];
      empfaenger?: Empfaenger;
      notiz?: string;
      bediener?: string;
      boniert?: BonierStand[];
    },
  ): Promise<OffenerBon> {
    return this.aufruf<{ bon: OffenerBon }>(
      'PUT',
      `/kassen/${encodeURIComponent(kassenId)}/tische/${encodeURIComponent(name)}`,
      // wie beim Beleg: ohne Fähigkeit lehnt der Server kg-Positionen ab (wa4 a48294f)
      { ...bon, faehigkeiten: KASSEN_FAEHIGKEITEN },
    ).then((r) => r.bon);
  }

  termine(kassenId: string, tag: string): Promise<{ tag: string; termine: Termin[] }> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/termine?tag=${encodeURIComponent(tag)}`,
    );
  }

  /** Neu (id null) oder ändern; 409 bei Überschneidung beim selben Mitarbeiter. */
  terminSpeichern(kassenId: string, id: number | null, t: TerminEingabe): Promise<Termin> {
    return this.aufruf<{ termin: Termin }>(
      id === null ? 'POST' : 'PUT',
      `/kassen/${encodeURIComponent(kassenId)}/termine${id === null ? '' : `/${String(id)}`}`,
      t,
    ).then((r) => r.termin);
  }

  async terminLoeschen(kassenId: string, id: number): Promise<void> {
    await this.aufruf<unknown>(
      'DELETE',
      `/kassen/${encodeURIComponent(kassenId)}/termine/${String(id)}`,
      undefined,
      true,
    );
  }

  tischplan(
    kassenId: string,
  ): Promise<{ raster: { breite: number; hoehe: number }; plan: Tischplan }> {
    return this.aufruf('GET', `/kassen/${encodeURIComponent(kassenId)}/tischplan`);
  }

  /** Neu bestellte (positiv) bzw. abbestellte (negativ) Mengen an Küche und Schank. */
  bonieren(
    kassenId: string,
    auftrag: {
      vorgangId?: string;
      tisch?: string;
      bediener?: string;
      notiz?: string;
      positionen: BonierStand[];
    },
  ): Promise<{
    bonierungen: (Bonierung & { gedruckt: boolean; druckFehler: string | null })[];
    ohneZiel: number;
  }> {
    return this.aufruf('POST', `/kassen/${encodeURIComponent(kassenId)}/bonierung`, auftrag);
  }

  /** Küchenmonitor: offene und zuletzt erledigte Bonierungen. */
  bonierungen(
    kassenId: string,
    ziel: string | null,
  ): Promise<{ ziele: string[]; offen: Bonierung[]; erledigt: Bonierung[] }> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/bonierungen${ziel === null ? '' : `?ziel=${encodeURIComponent(ziel)}`}`,
    );
  }

  bonierungErledigt(kassenId: string, id: number, erledigt: boolean): Promise<unknown> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/bonierungen/${String(id)}/erledigt`,
      { erledigt },
    );
  }

  /** Aufrufen liefert den Bon und löscht ihn am Server (404, wenn ein anderes Gerät schneller war). */
  /**
   * Online bezahlte Lieferdienst-Bestellung erfassen – kein Beleg, keine Signatur. Eine
   * Wiederholung mit gleichem Inhalt liefert den vorhandenen Eintrag (200).
   */
  plattformumsatzErfassen(
    kassenId: string,
    umsatz: {
      plattform: UmsatzPlattform;
      externeId: string;
      lieferart: 'lieferung' | 'abholung';
      positionen: Position[];
      bediener?: string;
      notiz?: string;
    },
  ): Promise<Plattformumsatz> {
    return this.aufruf<{ umsatz: Plattformumsatz }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/lieferdienste/umsaetze`,
      umsatz,
    ).then((r) => r.umsatz);
  }

  /**
   * Migration 0084: ungeklärte Lieferdienst-Bestellung endgültig als nicht erfasst sperren (nur
   * nach ausdrücklicher Prüfung durch eine benannte Person). Atomar am Server: schon erfasst →
   * „erfasst“ (keine Sperre); sonst „gesperrt“ – keine spätere Erfassung mehr möglich.
   */
  plattformumsatzSperren(
    kassenId: string,
    sperre: {
      plattform: UmsatzPlattform;
      externeId: string;
      vorgangId: string;
      grund: string;
      geprueftVon: string;
    },
  ): Promise<{ stand: 'gesperrt' } | { stand: 'erfasst'; umsatz: Plattformumsatz }> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/lieferdienste/sperre`,
      sperre,
    );
  }

  plattformumsaetze(
    kassenId: string,
    datum: string,
  ): Promise<{ umsaetze: Plattformumsatz[]; summen: PlattformSumme[] }> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/lieferdienste/umsaetze?datum=${encodeURIComponent(datum)}`,
    );
  }

  plattformumsatzStornieren(kassenId: string, id: number, grund: string): Promise<Plattformumsatz> {
    return this.aufruf<{ umsatz: Plattformumsatz }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/lieferdienste/umsaetze/${String(id)}/storno`,
      { grund },
    ).then((r) => r.umsatz);
  }

  /** Mit `vorgangId` liefert ein wiederholter Aufruf (Antwort verloren) denselben Bon. */
  tischAufrufen(kassenId: string, name: string, vorgangId?: string): Promise<OffenerBon> {
    return this.aufruf<{ bon: OffenerBon }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/tische/${encodeURIComponent(name)}/aufruf`,
      {
        ...(vorgangId === undefined ? {} : { vorgangId }),
        // ohne Fähigkeit bleibt ein Bon mit kg-Positionen geparkt (422)
        faehigkeiten: KASSEN_FAEHIGKEITEN,
      },
    ).then((r) => r.bon);
  }

  tagesbericht(kassenId: string, datum: string): Promise<Tagesbericht> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/berichte/tag?datum=${encodeURIComponent(datum)}`,
    );
  }

  /** Bediener des Unternehmers vom Server; `verwaltet` false = keine Verwaltung, lokale Namen gelten. */
  bedienerListe(kassenId: string): Promise<{ bediener: BedienerKurz[]; verwaltet: boolean }> {
    return this.aufruf('GET', `/kassen/${encodeURIComponent(kassenId)}/bediener`);
  }

  /** Bediener anmelden; 401 bei falscher/fehlender PIN, 429 nach Fehlversuchen. */
  /** Anmeldung mit PIN; `sitzung` ist der signierte Nachweis für Storno und Rückerstattung. */
  bedienerAnmelden(
    kassenId: string,
    id: number,
    pin?: string,
  ): Promise<BedienerKurz & { sitzung?: string }> {
    return this.aufruf<{ bediener: BedienerKurz; sitzung?: string }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/bediener/${String(id)}/anmeldung`,
      pin === undefined ? {} : { pin },
    ).then((r) => ({ ...r.bediener, ...(r.sitzung === undefined ? {} : { sitzung: r.sitzung }) }));
  }

  /**
   * PB-3: Bediener-Kennzeichen am Server widerrufen (Abmelden, Bedienerwechsel, Leerlauf).
   * `abgemeldet: false` = war schon ungültig, abgelaufen oder gehört nicht zu dieser Kasse.
   */
  bedienerAbmelden(kassenId: string, sitzung: string): Promise<{ abgemeldet: boolean }> {
    return this.aufruf<{ abgemeldet: boolean }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/bediener/abmeldung`,
      { sitzung },
    );
  }

  /** Kunden des Unternehmers suchen (Rechnungsempfänger); höchstens 20 Treffer. */
  kunden(kassenId: string, text: string): Promise<KundeKurz[]> {
    return this.aufruf<{ kunden: KundeKurz[] }>(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/kunden?text=${encodeURIComponent(text)}`,
    ).then((r) => r.kunden);
  }

  /** Artikelstamm (Schnellwahl) der Kasse vom Server; 404 = keiner hinterlegt. */
  artikelstamm(kassenId: string): Promise<Artikelstamm> {
    return this.aufruf('GET', `/kassen/${encodeURIComponent(kassenId)}/artikelstamm`);
  }

  /** Delta seit dem Cursor (P7); seit=0 = vollständiger Stand inkl. deaktivierter Zeilen. */
  artikelstammDelta(
    kassenId: string,
    seit: number,
    etag: string | null,
  ): Promise<{ status: 304 } | { status: 200; etag: string | null; daten: ArtikelstammDelta }> {
    return this.bedingt(
      `/kassen/${encodeURIComponent(kassenId)}/artikelstamm?seit=${String(seit)}`,
      etag,
    ).then((r) => (r.status === 304 ? r : { ...r, daten: r.daten as ArtikelstammDelta }));
  }

  /**
   * Geräteaktivierung (P9): Einmalcode gegen einen neuen Kassenschlüssel tauschen. Ohne
   * Anmeldung und ohne Anmeldesperre – gerade eine Kasse mit ungültigem Schlüssel braucht das.
   */
  async aktiviere(
    kassenId: string,
    code: string,
  ): Promise<{ kassenId: string; schluessel: string; unternehmer: { name: string } }> {
    const res = await this.fetchFn(`${this.basisUrl}/aktivierung`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ kassenId, code }),
    });
    const text = await res.text();
    let json: unknown = null;
    try {
      json = JSON.parse(text);
    } catch {
      // kein JSON
    }
    if (!res.ok) {
      const fehler = istRecord(json) && typeof json.fehler === 'string' ? json.fehler : text;
      throw new ApiFehler(res.status, fehler || `HTTP ${String(res.status)}`);
    }
    return json as { kassenId: string; schluessel: string; unternehmer: { name: string } };
  }

  /** Gerätestatus melden (P9) – ohne Geheimnisse. */
  async geraetestatus(kassenId: string, daten: Record<string, unknown>): Promise<void> {
    await this.aufruf('POST', `/kassen/${encodeURIComponent(kassenId)}/geraetestatus`, daten, true);
  }

  /** Schnellwahl-Profil der Kasse (P10); null = kein Profil (bisheriges Verhalten). */
  schnellwahlProfil(kassenId: string): Promise<KassenProfil | null> {
    return this.aufruf<{ profil: KassenProfil | null }>(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/schnellwahl`,
    ).then((r) => r.profil);
  }

  syncstand(kassenId: string): Promise<Syncstand> {
    return this.aufruf('GET', `/kassen/${encodeURIComponent(kassenId)}/syncstand`);
  }

  stammPruefsumme(kassenId: string): Promise<StammPruefsumme> {
    return this.aufruf('GET', `/kassen/${encodeURIComponent(kassenId)}/artikelstamm/pruefsumme`);
  }

  /**
   * Ereigniskanal (P7, Server-Sent Events über fetch mit Authorization). Ruft `beiEreignis` je
   * Ereignis auf und endet, wenn der Server schließt oder `signal` abbricht.
   */
  async ereignisse(
    kassenId: string,
    beiEreignis: (art: string, daten: unknown) => void,
    signal: AbortSignal,
  ): Promise<void> {
    const token = this.token();
    const gesperrt = this.anmeldung?.sperre(token) ?? null;
    if (gesperrt !== null) {
      throw new ApiFehler(gesperrt.status, gesperrt.meldung);
    }
    const res = await this.fetchFn(
      `${this.basisUrl}/kassen/${encodeURIComponent(kassenId)}/ereignisse`,
      {
        headers: {
          Accept: 'text/event-stream',
          ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
        },
        signal,
      },
    );
    if (!res.ok || res.body === null) {
      const text = await res.text().catch(() => '');
      // Nur 401 sperrt global – 429 ist hier die Kanalgrenze, keine Anmeldesperre
      if (res.status === 401 && token === this.token()) {
        let json: unknown = null;
        try {
          json = JSON.parse(text);
        } catch {
          // kein JSON
        }
        const warte = Number(res.headers.get('Retry-After'));
        this.anmeldung?.melde(
          res.status,
          token,
          istRecord(json) ? json : {},
          Number.isFinite(warte) && warte > 0 ? warte : null,
        );
      }
      throw new ApiFehler(res.status, `Ereigniskanal: HTTP ${String(res.status)}`);
    }
    await leseEreignisse(res.body, beiEreignis);
  }

  /** Artikelstamm des Unternehmers ersetzen (Betreiber-Token). */
  artikelstammErsetzen(unternehmerId: string, eingabe: ArtikelstammEingabe): Promise<Artikelstamm> {
    return this.aufruf(
      'PUT',
      `/unternehmer/${encodeURIComponent(unternehmerId)}/artikelstamm`,
      eingabe,
    );
  }

  /** Beleg über die Bridge auf dem Bondrucker im Laden drucken. */
  /** Kassenlade über die Bridge öffnen (ESC/POS-Impuls am Bondrucker). */
  schubladeOeffnen(kassenId: string): Promise<Geraeteauftrag> {
    return this.aufruf<{ auftrag: Geraeteauftrag }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/schublade`,
    ).then((r) => r.auftrag);
  }

  /** Beleg drucken; `kopie` kennzeichnet einen Nachdruck nur im Druckbild (kein neuer Beleg). */
  druckauftrag(
    kassenId: string,
    belegnummer: string,
    opt: { kopie?: boolean } = {},
  ): Promise<Geraeteauftrag> {
    return this.aufruf<{ auftrag: Geraeteauftrag }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/belege/${encodeURIComponent(belegnummer)}/druckauftrag${opt.kopie === true ? '?kopie=1' : ''}`,
    ).then((r) => r.auftrag);
  }

  /** Testbon über die Bridge dieser Kasse (Hardware-Abnahme; kein Beleg). */
  testdruck(kassenId: string): Promise<Geraeteauftrag> {
    return this.aufruf<{ auftrag: Geraeteauftrag }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/terminal/testdruck`,
    ).then((r) => r.auftrag);
  }

  /** Tagesbericht als Bon über die Bridge drucken. */
  tagesberichtDruckauftrag(kassenId: string, datum: string): Promise<Geraeteauftrag> {
    return this.aufruf<{ auftrag: Geraeteauftrag }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/berichte/tag/druckauftrag?datum=${encodeURIComponent(datum)}`,
    ).then((r) => r.auftrag);
  }

  /** Tagesabschluss am ZVT-Terminal anstoßen (Bridge führt ihn aus). */
  terminalTagesabschluss(kassenId: string): Promise<Geraeteauftrag> {
    return this.aufruf<{ auftrag: Geraeteauftrag }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/terminal/tagesabschluss`,
    ).then((r) => r.auftrag);
  }

  terminalAuftrag(kassenId: string, auftragId: string): Promise<Geraeteauftrag> {
    return this.aufruf<{ auftrag: Geraeteauftrag }>(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/terminal/auftraege/${encodeURIComponent(auftragId)}`,
    ).then((r) => r.auftrag);
  }

  /** Außerbetriebnahme: signierter Schlussbeleg, danach keine Belege mehr. */
  async ausserBetriebNehmen(kassenId: string, belegnummer: string): Promise<BelegAntwort> {
    const schluss = await this.aufruf<BelegAntwort>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/ausserbetriebnahme`,
      { belegnummer, ...(await this.zeugeFeld(kassenId)) },
    );
    await this.zeugeMerken(kassenId, schluss);
    return schluss;
  }

  ausfallBeginnen(kassenId: string, komponente: string, grund: string): Promise<unknown> {
    return this.aufruf('POST', `/kassen/${encodeURIComponent(kassenId)}/ausfall`, {
      komponente,
      grund,
    });
  }

  async ausfallBeenden(kassenId: string, belegnummer: string): Promise<unknown> {
    const r = await this.aufruf<{ sammelbeleg?: unknown }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/ausfall/ende`,
      { belegnummer, ...(await this.zeugeFeld(kassenId)) },
    );
    // R-12 Ü-2: Der Sammelbeleg ist der jüngste bestätigte Beleg
    await this.zeugeMerken(kassenId, r.sammelbeleg);
    return r;
  }

  /** Gepufferte Barverkäufe nach einer Verbindungsstörung nachsynchronisieren. */
  async offlineSynchronisieren(
    kassenId: string,
    daten: {
      beginn: string;
      grund?: string;
      belege: {
        belegnummer: string;
        zeitpunkt: string;
        positionen: Position[];
        zahlungsart: 'bar';
        empfaenger?: Empfaenger;
        notiz?: string;
        bediener?: string;
        kundeId?: number;
        bedienerSitzung?: string;
        gegebenCent?: number;
        trinkgeldCent?: number;
        /** N1: Vorgangskennung (Wiederholung nur über sie erkannt). */
        vorgang?: Vorgang;
      }[];
      sammelbelegnummer: string;
      /** P1-1: false = weiterer Stapel folgt (noch kein Sammelbeleg). */
      abschliessen?: boolean;
      /** P1-12: Ausfall, den eine leere Übertragung (Sammelbeleg-Wiederholung) abschließt. */
      ausfallId?: number;
      /** N1: Kennung des Nachsendelaufs (über Stapel und Unterbrechungen gleich). */
      lauf?: string;
    },
  ): Promise<OfflineSyncAntwort> {
    const r = await this.aufruf<OfflineSyncAntwort>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/offline-synchronisation`,
      {
        ...daten,
        belege: daten.belege.map((b) => ({ ...b, faehigkeiten: KASSEN_FAEHIGKEITEN })),
        ...(await this.zeugeFeld(kassenId)),
      },
    );
    // Codex R12-3: auch ein Zwischenstapel ohne Sammelbeleg ist bestätigt (`geraetezeuge` = jüngster
    // Beleg der Kasse) – gemerkt, bevor der Aufrufer den Puffer leert; nur vorwärts (R12-4).
    // N-4: scheitert das, wirft es – der Puffer bleibt (wie bei einer verlorenen Antwort)
    await this.zeugeMerken(kassenId, r.sammelbeleg, true);
    await this.zeugeMerken(kassenId, r.geraetezeuge, true);
    return r;
  }

  /** Kassenschlüssel ausstellen (Betreiber-Token nötig); nur einmal sichtbar. */
  async kassenschluesselAusstellen(kassenId: string): Promise<string> {
    const r = await this.aufruf<{ schluessel: string }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/kassenschluessel`,
    );
    return r.schluessel;
  }

  /**
   * N1: Gerätekennung der Kasse registrieren (`kennung` = Bestandskennung) bzw. eine neue
   * anfordern (ohne `kennung`). Der Server liefert die künftig zu verwendende Kennung.
   */
  meldeGeraetekennung(kassenId: string, kennung?: string): Promise<{ kennung: string }> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/geraetekennung`,
      kennung === undefined ? {} : { kennung },
    );
  }

  /**
   * S-1/F6: POST an die Druckversuch-Routen (`druckversuch.ts`, `DruckversuchHttp`). Jede
   * HTTP-Antwort kommt als Status und JSON zurück (409/422/503 sind Entscheidungen des Servers,
   * keine Ausnahmen); nur ohne Antwort (Netz, Zeitlimit, Status 0) wirft die Methode – die Quelle
   * druckt dann nicht.
   */
  async druckversuchSenden(
    pfad: string,
    body: unknown,
  ): Promise<{ status: number; daten: unknown }> {
    try {
      const daten = await this.aufruf<unknown>('POST', pfad, body);
      // aufruf liefert nur bei 2xx; 200 und 201 unterscheidet der Inhalt (`wiederholt`)
      return { status: istRecord(daten) && daten.wiederholt === true ? 200 : 201, daten };
    } catch (e: unknown) {
      if (e instanceof ApiFehler && e.status > 0) {
        return { status: e.status, daten: e.daten ?? {} };
      }
      throw e;
    }
  }

  /**
   * N1: Stand eines gesendeten Verkaufs – „gebucht“ (mit Beleg) und „verworfen“ sind endgültig,
   * „klaerung“ wartet auf das Backoffice, „unbekannt“ beweist nichts.
   */
  vorgangStand(kassenId: string, vorgangId: string): Promise<VorgangsStand> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/vorgaenge/${encodeURIComponent(vorgangId)}`,
    );
  }

  /**
   * N1: gesendeten Verkauf endgültig als nicht gebucht abschließen (Server prüft unter der
   * Kassensperre, dass kein Beleg besteht; ein verspäteter Aufruf bucht danach nie mehr).
   */
  /**
   * N1: offenen Bon einer Kassenversion vor N1 (ohne Vorgangs-ID) abschließen – der Server sperrt
   * die Nummer, wenn kein Beleg besteht („gesperrt“), sonst liefert er den Beleg („vergeben“).
   */
  belegnummerSperren(
    kassenId: string,
    belegnummer: string,
    grund: string,
  ): Promise<{ stand: 'gesperrt' } | { stand: 'vergeben'; beleg: BelegAntwort }> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/belegnummern/${encodeURIComponent(belegnummer)}/sperren`,
      { grund },
    );
  }

  vorgangVerwerfen(
    kassenId: string,
    vorgangId: string,
    belegnummer: string,
    grund: string,
  ): Promise<VorgangsStand> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/vorgaenge/${encodeURIComponent(vorgangId)}/verwerfen`,
      { belegnummer, grund },
    );
  }

  async erstelleBeleg(kassenId: string, auftrag: BelegAuftrag): Promise<BelegAntwort> {
    const beleg = await this.aufruf<BelegAntwort>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/belege`,
      { ...auftrag, faehigkeiten: KASSEN_FAEHIGKEITEN, ...(await this.zeugeFeld(kassenId)) },
    );
    // R-12 Ü-2: vorangestellte Pflichtbelege liegen in der Kette davor – dieser Beleg ist der jüngste
    await this.zeugeMerken(kassenId, beleg);
    return beleg;
  }

  /** Beleg per E-Mail an den Kunden: Belegtext (bon) oder Rechnung im A4-Layout (rechnung). */
  belegEmail(
    kassenId: string,
    belegnummer: string,
    an: string,
    form: 'bon' | 'rechnung' = 'bon',
    nachricht?: string,
  ): Promise<{ an: string; id: string }> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/belege/${encodeURIComponent(belegnummer)}/email`,
      { an, form, ...(nachricht === undefined ? {} : { nachricht }) },
    );
  }

  /** Gutscheinkarte über die Bridge auf dem Bondrucker drucken. */
  gutscheinDruckauftrag(kassenId: string, code: string): Promise<Geraeteauftrag> {
    return this.aufruf<{ auftrag: Geraeteauftrag }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/gutscheine/${encodeURIComponent(code)}/druckauftrag`,
    ).then((r) => r.auftrag);
  }

  /** Gutschein vor der Einlösung prüfen (Art, Rest, Gültigkeit). */
  gutschein(
    kassenId: string,
    code: string,
  ): Promise<{ gutschein: Gutschein; einloesbar: boolean }> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/gutscheine/${encodeURIComponent(code)}`,
    );
  }

  beleg(kassenId: string, belegnummer: string): Promise<BelegDetails> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/belege/${encodeURIComponent(belegnummer)}`,
    );
  }

  /** Belegjournal: neueste zuerst, optional Volltext (Belegnummer, Artikel, Notiz, Empfänger). */
  belegjournal(
    kassenId: string,
    filter: { suche?: string; limit?: number } = {},
  ): Promise<{ gesamt: number; belege: JournalBeleg[] }> {
    const p = new URLSearchParams();
    if (filter.suche !== undefined && filter.suche.trim() !== '') {
      p.set('suche', filter.suche.trim());
    }
    p.set('limit', String(filter.limit ?? 20));
    return this.aufruf('GET', `/kassen/${encodeURIComponent(kassenId)}/belege?${p.toString()}`);
  }

  druck(kassenId: string, belegnummer: string): Promise<string> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/belege/${encodeURIComponent(belegnummer)}/druck`,
      undefined,
      true,
    );
  }

  /** Kartenzahlung starten: `betragCent` ist der Terminalbetrag inklusive `trinkgeldCent`. */
  async starteZahlung(
    kassenId: string,
    betragCent: number,
    verwendungszweck?: string,
    trinkgeldCent = 0,
    /**
     * Sicherheitspaket S3: der zu bezahlende Verkauf – der Server prüft Rabatte und Preise
     * vor dem Terminal (Ablehnung → ApiFehler 403/422, keine Zahlung gestartet).
     */
    verkauf?: Pick<BelegAuftrag, 'kundeId' | 'gutscheine' | 'gutscheinAusgaben'> & {
      positionen: Position[];
    },
    /** P13: vor dem Aufruf gespeicherte Vorgangs-ID – eine Wiederholung startet nie neu. */
    vorgangId?: string,
  ): Promise<Zahlung> {
    const r = await this.aufruf<{ zahlung: Zahlung }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/zahlungen`,
      {
        betragCent,
        ...(trinkgeldCent > 0 ? { trinkgeldCent } : {}),
        ...(verwendungszweck === undefined ? {} : { verwendungszweck }),
        ...(verkauf === undefined
          ? {}
          : { verkauf: { ...verkauf, faehigkeiten: KASSEN_FAEHIGKEITEN } }),
        ...(vorgangId === undefined ? {} : { vorgangId }),
      },
    );
    return r.zahlung;
  }

  /**
   * P13: Stand eines Kartenzahlungsvorgangs. unbekannt/nicht_gestartet = sicher keine Zahlung;
   * reserviert = Start unklar; gestartet = genau diese Zahlung.
   */
  zahlungVorgang(
    kassenId: string,
    vorgangId: string,
  ): Promise<{
    stand:
      | 'unbekannt'
      | 'reserviert'
      | 'gestartet'
      | 'nicht_gestartet'
      | 'verworfen'
      | 'manuell_geklaert';
    zahlung?: Zahlung;
    belegnummer?: string | null;
  }> {
    return this.aufruf(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/zahlungen/vorgang/${encodeURIComponent(vorgangId)}`,
    );
  }

  /**
   * P13: Vorgang am Server abschließen, solange er nie reserviert wurde – danach startet auch
   * eine verspätete Startanfrage nicht mehr. 'reserviert' = Start bereits angekommen (unklar).
   */
  zahlungVorgangVerwerfen(
    kassenId: string,
    vorgangId: string,
  ): Promise<{ stand: 'verworfen' | 'reserviert' }> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/zahlungen/vorgang/${encodeURIComponent(vorgangId)}/verwerfen`,
    );
  }

  /**
   * P13: reservierten Vorgang abschließen (Terminal zeigt keine Zahlung). Der Server verlangt
   * eine Wartezeit nach dem Start (409 mit „frühestens“) und ggf. das Stornorecht.
   */
  zahlungVorgangAbschliessen(
    kassenId: string,
    vorgangId: string,
    /** Wer am Terminal bestätigt hat (Anzeigename) – wird im Klärungsvermerk festgehalten. */
    bestaetigtVon: string,
  ): Promise<{
    stand:
      | 'nicht_gestartet'
      | 'manuell_geklaert'
      | 'gestartet'
      | 'verworfen'
      | 'reserviert'
      | 'unbekannt';
  }> {
    return this.aufruf(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/zahlungen/vorgang/${encodeURIComponent(vorgangId)}/abschliessen`,
      { bestaetigtVon },
    );
  }

  /**
   * Manuelle Klärung einer übernommenen ZVT-Zahlung ohne verlässliches Ergebnis (Terminalbeleg
   * bzw. -journal geprüft). „bezahlt“ nur mit Terminal-Belegnummer; der Server prüft Frist,
   * Stornorecht und abweichenden Betrag.
   */
  async klaereZahlung(
    kassenId: string,
    zahlungsreferenz: string,
    klaerung: {
      ergebnis: 'bezahlt' | 'nicht_bezahlt';
      bestaetigtVon: string;
      terminalBelegnummer?: number;
    },
  ): Promise<Zahlung> {
    const r = await this.aufruf<{ zahlung: Zahlung }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/zahlungen/${encodeURIComponent(zahlungsreferenz)}/klaerung`,
      klaerung,
    );
    return r.zahlung;
  }

  /** Zahlungen der Kasse seit `seit` (ISO) mit abrechnendem Beleg – nach verlorener Startantwort. */
  async zahlungenSeit(kassenId: string, seit: string): Promise<ZahlungMitBeleg[]> {
    const r = await this.aufruf<{ zahlungen: ZahlungMitBeleg[] }>(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/zahlungen?seit=${encodeURIComponent(seit)}`,
    );
    return r.zahlungen;
  }

  async zahlung(kassenId: string, zahlungsreferenz: string): Promise<Zahlung> {
    const r = await this.aufruf<{ zahlung: Zahlung }>(
      'GET',
      `/kassen/${encodeURIComponent(kassenId)}/zahlungen/${encodeURIComponent(zahlungsreferenz)}`,
    );
    return r.zahlung;
  }

  async storniereZahlung(kassenId: string, zahlungsreferenz: string): Promise<Zahlung> {
    const r = await this.aufruf<{ zahlung: Zahlung }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/zahlungen/${encodeURIComponent(zahlungsreferenz)}/storno`,
    );
    return r.zahlung;
  }

  /** Rückerstattung (Kartenstorno) anstoßen; Ergebnis „erstattet" oder zunächst „ausstehend". */
  async erstatteZahlung(
    kassenId: string,
    zahlungsreferenz: string,
    betragCent?: number,
    bedienerSitzung?: string,
    /** Bezahlte Zahlung ohne Beleg: der Server prüft unter der Belegsperre, dass keiner existiert. */
    ohneBeleg = false,
  ): Promise<Zahlung> {
    const r = await this.aufruf<{ zahlung: Zahlung }>(
      'POST',
      `/kassen/${encodeURIComponent(kassenId)}/zahlungen/${encodeURIComponent(zahlungsreferenz)}/rueckerstattung`,
      {
        ...(betragCent === undefined ? {} : { betragCent }),
        ...(bedienerSitzung === undefined ? {} : { bedienerSitzung }),
        ...(ohneBeleg ? { ohneBeleg: true } : {}),
        // av5 Nachtrag 4 M-1a: Gerätezeuge wie am Beleg (im Modus `freigabe` Pflicht)
        ...(await this.zeugeFeld(kassenId)),
      },
    );
    return r.zahlung;
  }

  /**
   * Fragt den Stand der Zahlung ab, bis sie nicht mehr „ausstehend" ist.
   * Netzfehler zwischendurch werden toleriert (die Zahlung läuft am Terminal weiter).
   */
  async warteAufZahlung(
    kassenId: string,
    zahlungsreferenz: string,
    opt: {
      intervallMs?: number;
      timeoutMs?: number;
      signal?: AbortSignal;
      beiStand?: (z: Zahlung) => void;
      /**
       * Stand vorübergehend nicht abrufbar (Geräteanmeldung ungültig, Server-Sperre): das Warten
       * läuft weiter (ohne Serveranfrage, solange die Anmeldung gesperrt ist) und nimmt den
       * Abruf wieder auf, sobald die Anmeldung frei ist. Die Zahlungsreferenz bleibt erhalten.
       */
      beiUnterbrechung?: (meldung: string | null) => void;
    } = {},
  ): Promise<Zahlung> {
    const intervall = opt.intervallMs ?? 1_000;
    let frist = Date.now() + (opt.timeoutMs ?? 180_000);
    while (Date.now() < frist) {
      if (opt.signal?.aborted === true) {
        throw new ApiFehler(0, 'Warten auf die Zahlung abgebrochen');
      }
      const beginn = Date.now();
      let unterbrochen = false;
      try {
        const stand = await this.zahlung(kassenId, zahlungsreferenz);
        opt.beiUnterbrechung?.(null);
        opt.beiStand?.(stand);
        if (stand.status !== 'ausstehend') {
          return stand;
        }
      } catch (e: unknown) {
        if (e instanceof ApiFehler && (e.status === 401 || e.status === 429)) {
          unterbrochen = true;
          opt.beiUnterbrechung?.(e.message);
        } else if (e instanceof ApiFehler && e.status >= 400 && e.status < 500) {
          throw e;
        }
      }
      await new Promise((r) => setTimeout(r, intervall));
      if (unterbrochen) {
        // Während der Anmeldesperre pausiert die Frist: die offene Zahlung wird weiter
        // überwacht, bis der Stand wieder abrufbar ist (oder der Dialog abbricht).
        frist += Date.now() - beginn;
      }
    }
    throw new ApiFehler(0, 'Keine Rückmeldung zur Zahlung – Stand am Terminal prüfen');
  }

  /**
   * Retoure Stufe 2 (Kassenoberfläche): Abruf unter `/kassen/:id/retouren…`. Pfade und Auswertung
   * der Antworten liegen ausschließlich in `retoure/api.ts` (eine Stelle für den API-Vertrag);
   * hier nur Transport, Anmeldung und Zeitlimit wie bei jedem Aufruf. Nur Retourenpfade.
   */
  retoureAbruf<T>(
    methode: 'GET' | 'POST',
    kassenId: string,
    rest: string,
    body?: unknown,
  ): Promise<T> {
    if (!/^\/retouren(\/|\?|$)/.test(rest)) {
      return Promise.reject(new Error(`Kein Retourenpfad: ${rest}`));
    }
    return this.aufruf<T>(methode, `/kassen/${encodeURIComponent(kassenId)}${rest}`, body);
  }

  /**
   * Retoure abschließen: Der Server signiert den Retourenbeleg an dieser Kasse. Wie beim Verkauf
   * mit Gerätezeuge (R-12 Ü-2) – der Retourenbeleg ist danach der jüngste Beleg der Kette.
   * Kassierweg (ein Verkaufsstopp sendet nichts), Zeitlimit wie ein Beleg.
   * Vertrag: `docs/retoure/api-vertrag-stufe2.md` 4.4 (Branch `feature/retoure-api`).
   */
  async retoureAbschluss<T>(
    kassenId: string,
    rest: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    if (!/^\/retouren$/.test(rest)) {
      throw new Error(`Kein Retourenabschluss: ${rest}`);
    }
    const antwort = await this.aufruf<T>('POST', `/kassen/${encodeURIComponent(kassenId)}${rest}`, {
      ...body,
      // nur Felder des Vertrags (4.4): `geraetezeuge` optional wie beim Beleg
      ...(await this.zeugeFeld(kassenId)),
    });
    await this.zeugeMerken(kassenId, istRecord(antwort) ? antwort.beleg : undefined);
    return antwort;
  }
}
